import { Ability } from '@casl/ability';
import {
  Actions,
  Permission,
  ResultStatus,
  Role,
  Subjects,
} from '@dispatch/shared/types';
import {
  DrawerComponent,
  DrawerMeta,
  Form,
  RenderFuncProps,
  Section,
  SectionDivider,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { format } from 'date-fns';
import { isEmpty, isNull } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Permissions } from '../components/RolePermissions';

const schema = yup
  .object({
    name: yup.string().trim().required('Name is required'),
  })
  .required();

export type RoleForm = {
  name: string;
  permissions: string[];
};

export type RoleDetailsProps = {
  data?: Role;
  status: ResultStatus;
  onSave: (value: RoleForm) => void;
  permissions: Permission[] | string[];
  userPermissions: Ability<[Actions, Subjects]> | undefined;
  onDeactivate?: (value: Role) => void;
  onActivate?: (value: Role) => void;
};

export function RoleDetails({
  data,
  onSave,
  status,
  permissions,
  userPermissions,
  onDeactivate,
  onActivate,
}: RoleDetailsProps) {
  const params = useParams();
  const roleId = params['roleId'];
  const navigate = useNavigate();

  const formDefaults: RoleForm = {
    name: '',
    permissions: [],
  };

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...formDefaults },
  });

  const hasPermissions = !!userPermissions?.can('put', 'roles');

  React.useEffect(() => {
    if (!isEmpty(data)) {
      reset({
        permissions: data?.permissions as string[],
        name: data?.name,
      });
    } else {
      reset({
        ...formDefaults,
      });
    }
  }, [data]);

  const handleSave = () => {
    handleSubmit(onSave)();
  };

  const renderPermissions = () => {
    const formPermissions = watch('permissions');
    const rolePermissions: Permission[] = permissions as Permission[];
    return (
      <Permissions
        permissions={rolePermissions}
        hasPermissions={hasPermissions}
        formPermissions={formPermissions}
        setValue={setValue}
      />
    );
  };

  const permissionError = formErrors?.permissions as unknown;
  const permissionErrorMessage: { message: string } = permissionError as {
    message: string;
  };

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <>
          <Section title="Role Information">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  required
                  control={control}
                  errors={errors}
                  disabled={!hasPermissions}
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Section>
          <SectionDivider />
          <Section>
            <Grid container>
              <Grid item xs={6}>
                <Stack>
                  <p style={{ margin: 0 }}>Add User Rights</p>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '0.9375rem',
                      lineHeight: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      letterSpacing: '0.4px',
                      color: 'rgba(0, 0, 0, 0.54)',
                      padding: '0.625rem 0',
                    }}
                  >
                    These will be applied as the base level rights for this role
                    type.
                  </Typography>
                  {permissionErrorMessage && (
                    <p
                      style={{
                        fontSize: '0.75rem',
                        color: red[700],
                        margin: 0,
                      }}
                    >
                      {permissionErrorMessage?.message}
                    </p>
                  )}
                  {renderPermissions()}
                </Stack>
              </Grid>
            </Grid>
          </Section>
        </>
      );
    },
  };

  const renderTitle = () => {
    if (status !== 'loading' && data?.name) {
      return data?.name;
    }

    if (roleId) {
      return 'Role';
    }

    return 'Add a New Role';
  };

  const handleDeactivation = () => {
    if (!data) return;
    onDeactivate?.(data);
  };

  const handleActivation = () => {
    if (!data) return;
    onActivate?.(data);
  };

  return (
    <DrawerComponent
      status={status}
      showButtons={hasPermissions}
      showDeactivateButton={isNull(data?.deactivatedAt) && !isEmpty(data)}
      onDeactivate={handleDeactivation}
      showActivateButton={!isNull(data?.deactivatedAt) && !isEmpty(data)}
      onActivate={handleActivation}
      onSave={handleSave}
      saveDisabled={!isDirty}
      onClose={() => navigate('/admin/roles')}
      title={renderTitle()}
      subtitle={'ROLE'}
      sx={{ width: '55vw' }}
      showIsActive={!!data}
      isActive={isNull(data?.deactivatedAt)}
      metas={[
        <DrawerMeta
          title="Date Created"
          subtitle={
            data?.createdAt
              ? format(new Date(data?.createdAt), 'MM/dd/yyyy')
              : format(new Date(), 'MM/dd/yyyy')
          }
        />,
        <DrawerMeta
          title="Date Edited"
          subtitle={
            data?.updatedAt
              ? format(new Date(data?.updatedAt), 'MM/dd/yyyy')
              : '--'
          }
        />,
      ]}
    >
      <Form<RoleForm>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
    </DrawerComponent>
  );
}
